import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Users, Search, ArrowLeft, RefreshCw, CheckCircle, XCircle, Clock, Eye, Database, Trash2, AlertTriangle } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { useAuthStore } from '../stores/authStore';
import { cn } from '../utils/cn';

interface User {
  id: string;
  email: string;
  full_name: string;
  created_at: string;
  organization?: string;
}

interface PendingRegistration {
  id: string;
  user_email: string;
  user_name: string;
  organization: string;
  status: 'pending' | 'approved' | 'rejected';
  created_at: string;
}

interface AnalysisData {
  id: string;
  measurement_date: string;
  name: string;
  distance: number;
  data: {
    speed?: number;
    pitch?: number;
    stride?: number;
    contactTime?: number;
    flightTime?: number;
    predicted100m?: number | string;
  };
  comment?: string;
  created_at: string;
}

const AdminPage = () => {
  const navigate = useNavigate();
  const { isAuthenticated, isAdmin, checkSession, user: currentUser } = useAuthStore();
  const [users, setUsers] = useState<User[]>([]);
  const [pendingRegistrations, setPendingRegistrations] = useState<PendingRegistration[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [activeTab, setActiveTab] = useState<'users' | 'pending'>('users');
  const [processingAction, setProcessingAction] = useState<string | null>(null);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [userAnalysisData, setUserAnalysisData] = useState<AnalysisData[]>([]);
  const [loadingUserData, setLoadingUserData] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [userToDelete, setUserToDelete] = useState<User | null>(null);
  const [deletingUser, setDeletingUser] = useState(false);
  const [showArchivedRegistrations, setShowArchivedRegistrations] = useState(false);
  const [registrationToDelete, setRegistrationToDelete] = useState<PendingRegistration | null>(null);
  const [showDeleteRegistrationConfirm, setShowDeleteRegistrationConfirm] = useState(false);
  const [deletingRegistration, setDeletingRegistration] = useState(false);

  useEffect(() => {
    const checkAuthAndFetch = async () => {
      await checkSession();
      
      // Check if user is authenticated and admin
      if (!isAuthenticated) {
        console.log('User not authenticated, redirecting to login');
        navigate('/admin-login', { state: { from: '/admin' } });
        return;
      }

      if (!isAdmin) {
        console.log('User not admin, redirecting to dashboard');
        navigate('/');
        return;
      }

      fetchUsers();
      fetchPendingRegistrations();
    };
    
    checkAuthAndFetch();
  }, [isAuthenticated, isAdmin, navigate, checkSession]);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // First try to refresh the session
      const { data: { session }, error: refreshError } = await supabase.auth.getSession();
      if (refreshError || !session) {
        console.error('Session refresh failed:', refreshError || 'No session found');
        navigate('/admin-login', { state: { from: '/admin' } });
        return;
      }

      console.log('Fetching users with session:', session.user.email);

      // Fetch all profiles
      const { data, error: fetchError } = await supabase
        .from('profiles')
        .select('id, email, full_name, organization, created_at')
        .order('created_at', { ascending: false });

      if (fetchError) {
        console.error('Error fetching profiles:', fetchError);
        throw fetchError;
      }
      
      console.log('Fetched users:', data?.length || 0);
      setUsers(data || []);

    } catch (err) {
      console.error('Error fetching users:', err);
      setError(err instanceof Error ? err.message : 'ユーザー情報の取得に失敗しました');
    } finally {
      setLoading(false);
    }
  };

  const fetchPendingRegistrations = async () => {
    try {
      // Fetch pending registrations
      const { data, error: fetchError } = await supabase
        .from('registration_notifications')
        .select('*')
        .order('created_at', { ascending: false });

      if (fetchError) {
        console.error('Error fetching pending registrations:', fetchError);
        throw fetchError;
      }
      
      console.log('Fetched pending registrations:', data?.length || 0);
      setPendingRegistrations(data || []);

    } catch (err) {
      console.error('Error fetching pending registrations:', err);
      setError(err instanceof Error ? err.message : '承認待ちユーザーの取得に失敗しました');
    }
  };

  const fetchUserAnalysisData = async (userId: string) => {
    try {
      setLoadingUserData(true);
      setError(null);
      
      // サービスロールを使用してデータを取得
      const { data, error } = await supabase
        .from('analysis_data')
        .select('*')
        .eq('user_id', userId)
        .order('measurement_date', { ascending: false });
      
      if (error) {
        console.error('Error fetching user analysis data:', error);
        throw error;
      }
      
      console.log(`Fetched ${data?.length || 0} analysis records for user ${userId}`);
      setUserAnalysisData(data || []);
    } catch (err) {
      console.error('Error fetching user analysis data:', err);
      setError(err instanceof Error ? err.message : 'ユーザーデータの取得に失敗しました');
      setUserAnalysisData([]);
    } finally {
      setLoadingUserData(false);
    }
  };

  const handleViewUserData = async (user: User) => {
    setSelectedUser(user);
    await fetchUserAnalysisData(user.id);
  };

  const handleCloseUserData = () => {
    setSelectedUser(null);
    setUserAnalysisData([]);
  };

  const handleApproveRegistration = async (id: string, email: string) => {
    try {
      setProcessingAction(id);
      
      // Update registration status
      const { error: updateError } = await supabase
        .from('registration_notifications')
        .update({
          status: 'approved',
          approved_by: (await supabase.auth.getUser()).data.user?.id,
          approved_at: new Date().toISOString()
        })
        .eq('id', id);

      if (updateError) {
        throw updateError;
      }

      // Send approval notification to user
      try {
        const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/notify-user-approval`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`
          },
          body: JSON.stringify({ userEmail: email })
        });
        
        const responseData = await response.json();
        
        if (!response.ok) {
          console.warn('Failed to send user approval notification:', responseData);
        } else {
          console.log('User approval notification sent:', responseData);
        }
      } catch (notifyError) {
        console.error('Error sending user approval notification:', notifyError);
      }

      // Refresh the list
      fetchPendingRegistrations();
      
      alert('ユーザーを承認しました');
    } catch (err) {
      console.error('Error approving registration:', err);
      alert('承認処理に失敗しました: ' + (err instanceof Error ? err.message : '不明なエラー'));
    } finally {
      setProcessingAction(null);
    }
  };

  const handleRejectRegistration = async (id: string, email: string) => {
    try {
      setProcessingAction(id);
      
      // Update registration status
      const { error: updateError } = await supabase
        .from('registration_notifications')
        .update({
          status: 'rejected',
          approved_by: (await supabase.auth.getUser()).data.user?.id,
          approved_at: new Date().toISOString()
        })
        .eq('id', id);

      if (updateError) {
        throw updateError;
      }

      // Send rejection notification to user
      try {
        const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/notify-user-rejection`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`
          },
          body: JSON.stringify({ userEmail: email })
        });
        
        const responseData = await response.json();
        
        if (!response.ok) {
          console.warn('Failed to send user rejection notification:', responseData);
        } else {
          console.log('User rejection notification sent:', responseData);
        }
      } catch (notifyError) {
        console.error('Error sending user rejection notification:', notifyError);
      }

      // Refresh the list
      fetchPendingRegistrations();
      
      alert('ユーザーを拒否しました');
    } catch (err) {
      console.error('Error rejecting registration:', err);
      alert('拒否処理に失敗しました: ' + (err instanceof Error ? err.message : '不明なエラー'));
    } finally {
      setProcessingAction(null);
    }
  };

  const handleDeleteUser = async (user: User) => {
    setUserToDelete(user);
    setShowDeleteConfirm(true);
  };

  const confirmDeleteUser = async () => {
    if (!userToDelete) return;
    
    try {
      setDeletingUser(true);
      setError(null);

      // 管理者自身は削除できないようにする
      if (currentUser && userToDelete.email === currentUser.email) {
        throw new Error('自分自身のアカウントは削除できません');
      }

      // Edge Functionを使用してユーザーを削除
      const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/delete-user`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`
        },
        body: JSON.stringify({ email: userToDelete.email })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'ユーザーの削除に失敗しました');
      }

      // 成功したら一覧を更新
      await fetchUsers();
      
      alert(`ユーザー「${userToDelete.full_name || userToDelete.email}」を削除しました`);
      setShowDeleteConfirm(false);
      setUserToDelete(null);
      
      // 選択中のユーザーが削除されたユーザーだった場合、詳細表示を閉じる
      if (selectedUser && selectedUser.id === userToDelete.id) {
        handleCloseUserData();
      }
    } catch (err) {
      console.error('Error deleting user:', err);
      setError(err instanceof Error ? err.message : 'ユーザーの削除に失敗しました');
    } finally {
      setDeletingUser(false);
    }
  };

  const cancelDeleteUser = () => {
    setShowDeleteConfirm(false);
    setUserToDelete(null);
  };

  const handleDeleteRegistration = (registration: PendingRegistration) => {
    setRegistrationToDelete(registration);
    setShowDeleteRegistrationConfirm(true);
  };

  const confirmDeleteRegistration = async () => {
    if (!registrationToDelete) return;
    
    try {
      setDeletingRegistration(true);
      setError(null);

      // 登録申請を削除
      const { error: deleteError } = await supabase
        .from('registration_notifications')
        .delete()
        .eq('id', registrationToDelete.id);

      if (deleteError) {
        throw deleteError;
      }

      // 承認待ちユーザーの場合、関連するユーザーアカウントも削除
      if (registrationToDelete.status === 'pending') {
        try {
          // Edge Functionを使用してユーザーを削除
          const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/delete-user`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`
            },
            body: JSON.stringify({ email: registrationToDelete.user_email })
          });

          if (!response.ok) {
            const errorData = await response.json();
            console.warn('Failed to delete user account:', errorData);
          }
        } catch (userDeleteError) {
          console.warn('Error deleting user account:', userDeleteError);
        }
      }

      // 成功したら一覧を更新
      await fetchPendingRegistrations();
      
      alert(`申請「${registrationToDelete.user_name || registrationToDelete.user_email}」を削除しました`);
      setShowDeleteRegistrationConfirm(false);
      setRegistrationToDelete(null);
    } catch (err) {
      console.error('Error deleting registration:', err);
      setError(err instanceof Error ? err.message : '申請の削除に失敗しました');
    } finally {
      setDeletingRegistration(false);
    }
  };

  const cancelDeleteRegistration = () => {
    setShowDeleteRegistrationConfirm(false);
    setRegistrationToDelete(null);
  };

  const toggleArchivedRegistrations = () => {
    setShowArchivedRegistrations(!showArchivedRegistrations);
  };

  const filteredUsers = users.filter(user => 
    user.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.full_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.organization?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // フィルタリングされた登録申請
  const filteredPendingRegistrations = pendingRegistrations.filter(reg => {
    // 検索条件に一致するか
    const matchesSearch = 
      reg.user_email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      reg.user_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      reg.organization?.toLowerCase().includes(searchTerm.toLowerCase());
    
    // アーカイブ表示の条件
    const statusCondition = showArchivedRegistrations 
      ? true  // アーカイブ表示がオンの場合はすべて表示
      : reg.status === 'pending';  // オフの場合は保留中のみ表示
    
    return matchesSearch && statusCondition;
  });

  if (loading && users.length === 0 && pendingRegistrations.length === 0) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="text-lg text-gray-600">読み込み中...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="bg-white rounded-lg shadow-lg overflow-hidden">
          {/* Header */}
          <div className="border-b border-gray-200">
            <div className="p-6">
              <div className="flex items-center justify-between">
                <h1 className="text-2xl font-bold text-gray-900 flex items-center">
                  <Users className="w-6 h-6 mr-2" />
                  管理者ページ
                </h1>
                <div className="flex space-x-4">
                  <button
                    onClick={() => {
                      fetchUsers();
                      fetchPendingRegistrations();
                    }}
                    className="btn btn-secondary flex items-center"
                  >
                    <RefreshCw className="w-4 h-4 mr-2" />
                    更新
                  </button>
                  <button
                    onClick={() => navigate('/')}
                    className="btn btn-primary flex items-center"
                  >
                    <ArrowLeft className="w-4 h-4 mr-2" />
                    ダッシュボードに戻る
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Tabs */}
          <div className="border-b border-gray-200">
            <nav className="-mb-px flex">
              <button
                onClick={() => setActiveTab('users')}
                className={cn(
                  "w-1/2 py-4 px-1 text-center border-b-2 font-medium text-sm",
                  activeTab === 'users'
                    ? "border-primary-500 text-primary-600"
                    : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                )}
              >
                登録メンバー一覧
              </button>
              <button
                onClick={() => setActiveTab('pending')}
                className={cn(
                  "w-1/2 py-4 px-1 text-center border-b-2 font-medium text-sm",
                  activeTab === 'pending'
                    ? "border-primary-500 text-primary-600"
                    : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                )}
              >
                承認待ちユーザー
                {pendingRegistrations.filter(r => r.status === 'pending').length > 0 && (
                  <span className="ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                    {pendingRegistrations.filter(r => r.status === 'pending').length}
                  </span>
                )}
              </button>
            </nav>
          </div>

          {/* Search */}
          <div className="p-6 border-b border-gray-200 bg-gray-50">
            <div className="flex justify-between items-center">
              <div className="relative w-64">
                <input
                  type="text"
                  placeholder="検索..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              </div>
              
              {activeTab === 'pending' && (
                <div className="flex items-center">
                  <label className="inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={showArchivedRegistrations}
                      onChange={toggleArchivedRegistrations}
                      className="sr-only peer"
                    />
                    <div className="relative w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary-300 rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary-600"></div>
                    <span className="ms-3 text-sm font-medium text-gray-700">
                      アーカイブを表示
                    </span>
                  </label>
                </div>
              )}
            </div>
          </div>

          {/* Content */}
          <div className="p-6">
            {error && (
              <div className="mb-4 bg-red-50 text-red-700 p-4 rounded-lg">
                {error}
              </div>
            )}

            {/* Delete User Confirmation Modal */}
            {showDeleteConfirm && userToDelete && (
              <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                <div className="bg-white rounded-lg p-6 max-w-md w-full">
                  <div className="flex items-center justify-center mb-4 text-red-600">
                    <AlertTriangle className="h-12 w-12" />
                  </div>
                  <h3 className="text-lg font-medium text-gray-900 mb-2 text-center">ユーザーを削除しますか？</h3>
                  <p className="text-sm text-gray-500 mb-4 text-center">
                    「{userToDelete.full_name || userToDelete.email}」を削除します。この操作は元に戻せず、ユーザーのすべてのデータが削除されます。
                  </p>
                  <div className="flex justify-center space-x-4">
                    <button
                      onClick={cancelDeleteUser}
                      disabled={deletingUser}
                      className="btn btn-secondary"
                    >
                      キャンセル
                    </button>
                    <button
                      onClick={confirmDeleteUser}
                      disabled={deletingUser}
                      className="btn bg-red-600 text-white hover:bg-red-700 flex items-center"
                    >
                      {deletingUser ? (
                        <>
                          <div className="animate-spin mr-2 h-4 w-4 border-2 border-white border-t-transparent rounded-full"></div>
                          削除中...
                        </>
                      ) : (
                        <>
                          <Trash2 className="w-4 h-4 mr-2" />
                          削除する
                        </>
                      )}
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Delete Registration Confirmation Modal */}
            {showDeleteRegistrationConfirm && registrationToDelete && (
              <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                <div className="bg-white rounded-lg p-6 max-w-md w-full">
                  <div className="flex items-center justify-center mb-4 text-red-600">
                    <AlertTriangle className="h-12 w-12" />
                  </div>
                  <h3 className="text-lg font-medium text-gray-900 mb-2 text-center">申請を削除しますか？</h3>
                  <p className="text-sm text-gray-500 mb-4 text-center">
                    「{registrationToDelete.user_name || registrationToDelete.user_email}」の申請を削除します。この操作は元に戻せません。
                    {registrationToDelete.status === 'pending' && '承認待ちユーザーの場合、関連するアカウントも削除されます。'}
                  </p>
                  <div className="flex justify-center space-x-4">
                    <button
                      onClick={cancelDeleteRegistration}
                      disabled={deletingRegistration}
                      className="btn btn-secondary"
                    >
                      キャンセル
                    </button>
                    <button
                      onClick={confirmDeleteRegistration}
                      disabled={deletingRegistration}
                      className="btn bg-red-600 text-white hover:bg-red-700 flex items-center"
                    >
                      {deletingRegistration ? (
                        <>
                          <div className="animate-spin mr-2 h-4 w-4 border-2 border-white border-t-transparent rounded-full"></div>
                          削除中...
                        </>
                      ) : (
                        <>
                          <Trash2 className="w-4 h-4 mr-2" />
                          削除する
                        </>
                      )}
                    </button>
                  </div>
                </div>
              </div>
            )}

            {selectedUser ? (
              <div className="space-y-6">
                <div className="flex justify-between items-center">
                  <h2 className="text-xl font-semibold text-gray-800">
                    ユーザープロフィール: {selectedUser.full_name || selectedUser.email}
                  </h2>
                  <div className="flex space-x-3">
                    <button
                      onClick={() => handleDeleteUser(selectedUser)}
                      className="btn bg-red-600 text-white hover:bg-red-700 flex items-center"
                      disabled={currentUser?.email === selectedUser.email}
                    >
                      <Trash2 className="w-4 h-4 mr-2" />
                      ユーザー削除
                    </button>
                    <button
                      onClick={handleCloseUserData}
                      className="btn btn-secondary"
                    >
                      戻る
                    </button>
                  </div>
                </div>
                
                <div className="bg-gray-50 p-6 rounded-lg">
                  <h3 className="text-lg font-medium text-gray-900 mb-4">プロフィール情報</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <p className="text-sm text-gray-500">名前</p>
                      <p className="font-medium">{selectedUser.full_name || '-'}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">メールアドレス</p>
                      <p className="font-medium">{selectedUser.email}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">所属組織</p>
                      <p className="font-medium">{selectedUser.organization || '-'}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">登録日</p>
                      <p className="font-medium">{new Date(selectedUser.created_at).toLocaleDateString()}</p>
                    </div>
                  </div>
                </div>

                <div>
                  <h3 className="text-lg font-medium text-gray-900 mb-4">分析データ</h3>
                  {loadingUserData ? (
                    <div className="text-center py-8">
                      <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
                      <p className="mt-2 text-gray-600">データを読み込み中...</p>
                    </div>
                  ) : userAnalysisData.length > 0 ? (
                    <div className="overflow-x-auto">
                      <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">測定日</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">名前</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">距離 (m)</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">スピード (m/s)</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">ピッチ (歩/秒)</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">ストライド (m)</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">コメント</th>
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                          {userAnalysisData.map((data) => (
                            <tr key={data.id}>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                {new Date(data.measurement_date).toLocaleDateString()}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{data.name}</td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{data.distance}</td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                {data.data.speed?.toFixed(2) || '-'}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                {data.data.pitch?.toFixed(2) || '-'}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                {data.data.stride?.toFixed(2) || '-'}
                              </td>
                              <td className="px-6 py-4 text-sm text-gray-900">{data.comment || '-'}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <div className="text-center py-8 bg-gray-50 rounded-lg">
                      <Database className="h-12 w-12 text-gray-400 mx-auto mb-3" />
                      <p className="text-gray-500">このユーザーの分析データはありません</p>
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <>
                {activeTab === 'users' && (
                  <div className="overflow-x-auto">
                    <h2 className="text-xl font-semibold text-gray-800 mb-4">登録メンバー一覧</h2>
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            名前
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            メールアドレス
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            所属組織
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            登録日
                          </th>
                          <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                            操作
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {filteredUsers.length > 0 ? (
                          filteredUsers.map((user) => (
                            <tr key={user.id}>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <div className="text-sm font-medium text-gray-900">
                                  {user.full_name || '-'}
                                </div>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <div className="text-sm text-gray-500">
                                  {user.email}
                                </div>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <div className="text-sm text-gray-500">
                                  {user.organization || '-'}
                                </div>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                {new Date(user.created_at).toLocaleDateString()}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                                <div className="flex justify-end space-x-3">
                                  <button
                                    onClick={() => handleViewUserData(user)}
                                    className="text-primary-600 hover:text-primary-900 flex items-center"
                                  >
                                    <Eye className="w-4 h-4 mr-1" />
                                    <span>詳細</span>
                                  </button>
                                  <button
                                    onClick={() => handleDeleteUser(user)}
                                    className="text-red-600 hover:text-red-900 flex items-center"
                                    disabled={currentUser?.email === user.email}
                                  >
                                    <Trash2 className="w-4 h-4 mr-1" />
                                    <span>削除</span>
                                  </button>
                                </div>
                              </td>
                            </tr>
                          ))
                        ) : (
                          <tr>
                            <td colSpan={5} className="px-6 py-4 text-center text-sm text-gray-500">
                              {searchTerm ? 'ユーザーが見つかりませんでした' : 'ユーザーが登録されていません'}
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                )}

                {activeTab === 'pending' && (
                  <div className="overflow-x-auto">
                    <h2 className="text-xl font-semibold text-gray-800 mb-4">
                      {showArchivedRegistrations ? '全ての申請' : '承認待ちユーザー'}
                    </h2>
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            名前
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            メールアドレス
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            所属組織
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            申請日
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            ステータス
                          </th>
                          <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                            操作
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {filteredPendingRegistrations.length > 0 ? (
                          filteredPendingRegistrations.map((registration) => (
                            <tr key={registration.id}>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <div className="text-sm font-medium text-gray-900">
                                  {registration.user_name || '-'}
                                </div>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <div className="text-sm text-gray-500">
                                  {registration.user_email}
                                </div>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <div className="text-sm text-gray-500">
                                  {registration.organization || '-'}
                                </div>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                {new Date(registration.created_at).toLocaleDateString()}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <span className={cn(
                                  "px-2 inline-flex text-xs leading-5 font-semibold rounded-full",
                                  registration.status === 'pending' 
                                    ? "bg-yellow-100 text-yellow-800" 
                                    : registration.status === 'approved'
                                    ? "bg-green-100 text-green-800"
                                    : "bg-red-100 text-red-800"
                                )}>
                                  {registration.status === 'pending' && (
                                    <>
                                      <Clock className="w-3 h-3 mr-1" />
                                      承認待ち
                                    </>
                                  )}
                                  {registration.status === 'approved' && (
                                    <>
                                      <CheckCircle className="w-3 h-3 mr-1" />
                                      承認済み
                                    </>
                                  )}
                                  {registration.status === 'rejected' && (
                                    <>
                                      <XCircle className="w-3 h-3 mr-1" />
                                      拒否
                                    </>
                                  )}
                                </span>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                                <div className="flex justify-end space-x-2">
                                  {registration.status === 'pending' && (
                                    <>
                                      <button
                                        onClick={() => handleApproveRegistration(registration.id, registration.user_email)}
                                        className={cn(
                                          "text-green-600 hover:text-green-900",
                                          processingAction === registration.id && "opacity-50 cursor-not-allowed"
                                        )}
                                        disabled={processingAction === registration.id}
                                      >
                                        {processingAction === registration.id ? '処理中...' : '承認'}
                                      </button>
                                      <button
                                        onClick={() => handleRejectRegistration(registration.id, registration.user_email)}
                                        className={cn(
                                          "text-red-600 hover:text-red-900",
                                          processingAction === registration.id && "opacity-50 cursor-not-allowed"
                                        )}
                                        disabled={processingAction === registration.id}
                                      >
                                        {processingAction === registration.id ? '処理中...' : '拒否'}
                                      </button>
                                    </>
                                  )}
                                  <button
                                    onClick={() => handleDeleteRegistration(registration)}
                                    className="text-red-600 hover:text-red-900 flex items-center"
                                  >
                                    <Trash2 className="w-4 h-4 mr-1" />
                                    <span>削除</span>
                                  </button>
                                </div>
                              </td>
                            </tr>
                          ))
                        ) : (
                          <tr>
                            <td colSpan={6} className="px-6 py-4 text-center text-sm text-gray-500">
                              {searchTerm 
                                ? '申請が見つかりませんでした' 
                                : showArchivedRegistrations 
                                  ? '申請データがありません' 
                                  : '承認待ちユーザーはいません'}
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminPage;